import type { CosmeticCatalogEntry, UserLoadoutDTO, WalletDTO, AccountDTO } from '@push.io/shared';
import { readScopedToken } from '../session/storage.js';
import type { AccountApi, EquipCosmeticRequest, InventoryEntry, UnequipCosmeticRequest } from './types.js';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers ?? {});
  headers.set('Content-Type', 'application/json');
  const token = readScopedToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(path, {
    ...init,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error || `http_${response.status}`);
  }

  return response.json() as Promise<T>;
}

export function createAccountApi(): AccountApi {
  return {
    getCatalog(): Promise<CosmeticCatalogEntry[]> {
      return request('/api/cosmetics/catalog');
    },
    getInventory(): Promise<InventoryEntry[]> {
      return request('/api/me/cosmetics');
    },
    getLoadout(): Promise<UserLoadoutDTO | null> {
      return request('/api/me/loadout');
    },
    getWallet(): Promise<WalletDTO> {
      return request('/api/me/wallet');
    },
    getAccount(): Promise<AccountDTO> {
      return request('/api/account');
    },
    updatePlayerName(name: string): Promise<AccountDTO> {
      return request('/api/account/name', {
        method: 'PUT',
        body: JSON.stringify({ name }),
      });
    },
    logout(): Promise<void> {
      return request('/api/auth/logout', { method: 'POST' });
    },
    grantAllCosmetics(): Promise<{ granted: number; inventory: InventoryEntry[]; loadout: UserLoadoutDTO | null }> {
      return request('/api/dev/grant-all-cosmetics', {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
    equipCosmetic(payload: EquipCosmeticRequest): Promise<UserLoadoutDTO> {
      return request('/api/me/loadout/equip', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    unequipCosmetic(payload: UnequipCosmeticRequest): Promise<UserLoadoutDTO> {
      return request('/api/me/loadout/unequip', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    setAccessoryColor(accessoryColor: string | null): Promise<UserLoadoutDTO> {
      return request('/api/me/loadout/accessory-color', {
        method: 'PUT',
        body: JSON.stringify({ accessoryColor }),
      });
    },
    purchase(cosmeticId: string): Promise<{ wallet: WalletDTO; loadout: UserLoadoutDTO }> {
      return request('/api/me/cosmetics/purchase', {
        method: 'POST',
        body: JSON.stringify({ cosmeticId }),
      });
    },
  };
}
